import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styles from './NewComplaintModal.module.css';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import { OrderSearchResult, ComplaintItem, createComplaint, CreateComplaintPayload, uploadComplaintImage } from '../services/complaintsAPI';
import OrderSelectionStep from './Wizard/OrderSelectionStep';
import ItemSelectionStep from './Wizard/ItemSelectionStep';
import ImageUploadStep from './Wizard/ImageUploadStep';
import ReviewStep from './Wizard/ReviewStep';
import { useNotificationToasts } from '../../../hooks/useNotificationToasts';
import { NotificationToastContainer } from '../../../components/NotificationToast/NotificationToast';
import { useAuthStore } from '../../../stores/authStore';
import { useUploadStore } from '../../../stores/uploadStore';

interface NewComplaintModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export interface ComplaintWizardState {
    order: OrderSearchResult | null;
    items: ComplaintItem[];
    images: File[];
    labelImages: File[]; // Fotos das etiquetas (obrigatório)
    tipoRessarcimento: 'CREDITO' | 'DESCONTO' | 'REPOSICAO' | 'NENHUM';
}



interface StepProps {
    data: ComplaintWizardState;
    updateData: (updates: Partial<ComplaintWizardState>) => void;
    goToStep?: (step: number) => void;
    highlightRefund?: boolean;
}

const STEPS = [
    { id: 1, label: 'Pedido' },
    { id: 2, label: 'Itens' },
    { id: 3, label: 'Fotos' },
    { id: 4, label: 'Revisão' }
];

const SESSION_STORAGE_KEY = 'complaint_wizard_draft';

const NewComplaintModal: React.FC<NewComplaintModalProps> = ({ isOpen, onClose }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showLabelError, setShowLabelError] = useState(false);
    const [showImageError, setShowImageError] = useState(false);
    const [activeComplaintId, setActiveComplaintId] = useState<number | null>(null);
    const [createdComplaintId, setCreatedComplaintId] = useState<number | null>(null);
    const { addToast, toasts, removeToast } = useNotificationToasts();
    const user = useAuthStore(state => state.user);
    const startUpload = useUploadStore(state => state.startUpload);
    const uploadTasks = useUploadStore(state => state.tasks);
    const [wizardData, setWizardData] = useState<ComplaintWizardState>(() => {
        // Carregar rascunho inicial
        const saved = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return {
                    ...parsed,
                    images: [], // Não é possível serializar arquivos
                    labelImages: [] // Não é possível serializar arquivos
                };
            } catch (e) {
                console.error("Erro ao carregar rascunho", e);
            }
        }
        return {
            order: null,
            items: [],
            images: [],
            labelImages: [],
            tipoRessarcimento: 'NENHUM',
        };
    });

    // Salvar rascunho sempre que mudar (exceto imagens)
    useEffect(() => {
        const { images, labelImages, ...rest } = wizardData;
        if (rest.order || rest.items.length > 0) {
            sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(rest));
        }
    }, [wizardData]);

    const updateData = (updates: Partial<ComplaintWizardState>) => {
        setWizardData(prev => ({ ...prev, ...updates }));
    };

    const goToStep = (step: number) => {
        if (step >= 1 && step <= 4) setCurrentStep(step);
    };

    const handleNext = useCallback(async () => {
        // Validation Logic
        if (currentStep === 1 && !wizardData.order) {
            addToast({
                id: 'no-order',
                type: 'alert',
                title: 'Atenção',
                description: 'Por favor, selecione um pedido para continuar.',
                time: 'Agora',
                read: false
            });
            return;
        }
        if (currentStep === 2) {
            if (wizardData.items.length === 0) {
                addToast({
                    id: 'no-items',
                    type: 'alert',
                    title: 'Itens Necessários',
                    description: 'Adicione pelo menos um item à reclamação.',
                    time: 'Agora',
                    read: false
                });
                return;
            }
            if (!wizardData.tipoRessarcimento || wizardData.tipoRessarcimento === 'NENHUM') {
                addToast({
                    id: 'no-refund-type',
                    type: 'alert',
                    title: 'Tipo de Ressarcimento',
                    description: 'Por favor, selecione uma opção de ressarcimento.',
                    time: 'Agora',
                    read: false
                });
                return;
            }
            setCurrentStep(curr => curr + 1);
            return;
        }

        if (currentStep === 3) {
            let hasError = false;
            if (wizardData.images.length === 0) {
                setShowImageError(true);
                addToast({
                    id: 'no-images',
                    type: 'alert',
                    title: 'Foto Necessária',
                    description: 'Por favor, anexe a(s) foto(s) da evidência do produto.',
                    time: 'Agora',
                    read: false
                });
                hasError = true;
            }
            if (wizardData.labelImages.length === 0) {
                setShowLabelError(true);
                addToast({
                    id: 'no-labels',
                    type: 'alert',
                    title: 'Etiqueta Necessária',
                    description: 'Por favor, anexe a(s) foto(s) da(s) etiqueta(s) do(s) produto(s).',
                    time: 'Agora',
                    read: false
                });
                hasError = true;
            }

            if (hasError) {
                setTimeout(() => {
                    setShowImageError(false);
                    setShowLabelError(false);
                }, 3000);
                return;
            }
        }



        if (currentStep < 4) setCurrentStep(curr => curr + 1);
        else {
            // Submit to real backend
            if (!wizardData.order || !user) return;

            setIsSubmitting(true);

            const payload: CreateComplaintPayload = {
                vendaId: wizardData.order.id,
                clienteId: wizardData.order.clientId,
                vendedorId: Number(user.id),
                segmento: wizardData.order.segment || null,
                tipoRessarcimento: wizardData.tipoRessarcimento,
                totalImages: wizardData.images.length + wizardData.labelImages.length,
                items: wizardData.items.map(item => ({
                    produtoId: item.productId,
                    referencia: item.productRef,
                    descricao: item.productDesc,
                    ca: item.ca || null,
                    lote: item.batch || null,
                    observacao: item.observation || null,
                    paresComprados: item.totalPurchased,
                    quantidades: item.quantities
                }))
            };

            try {
                let complaintId = createdComplaintId;

                // 1. Create Complaint (ONLY if not created yet)
                if (!complaintId) {
                    const newComplaint = await createComplaint(payload);
                    complaintId = newComplaint.id;
                    setCreatedComplaintId(complaintId);
                }

                // 2. Upload Images (if any)
                const totalImgs = wizardData.images.length + wizardData.labelImages.length;
                if (totalImgs > 0 && complaintId) {
                    setActiveComplaintId(complaintId);
                    await startUpload(complaintId, wizardData.images, wizardData.labelImages);
                }

                addToast({
                    id: 'success-submit',
                    type: 'success',
                    title: 'Enviado',
                    description: 'Reclamação registrada com sucesso!',
                    time: 'Agora',
                    read: false
                });

                // Limpar todos os dados do rascunho
                sessionStorage.removeItem(SESSION_STORAGE_KEY);
                setCreatedComplaintId(null); // Reset for next use
                setWizardData({
                    order: null,
                    items: [],
                    images: [],
                    labelImages: [],
                    tipoRessarcimento: 'NENHUM'
                });
                setCurrentStep(1);

                // Close modal right away
                onClose();
            } catch (error: any) {
                console.error('Erro ao criar reclamação:', error);
                const errorMessage = error?.response?.data?.error || 'Erro ao enviar reclamação. Tente novamente.';
                addToast({
                    id: 'error-submit',
                    type: 'alert',
                    title: 'Erro',
                    description: errorMessage,
                    time: 'Agora',
                    read: false
                });
            } finally {
                setIsSubmitting(false);
                setActiveComplaintId(null);
            }
        }
    }, [currentStep, wizardData, onClose, addToast, user]);

    const handleBack = useCallback(() => {
        if (currentStep > 1) setCurrentStep(curr => curr - 1);
    }, [currentStep]);

    // Atalhos de teclado
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (isSubmitting) return; // Ignore keys while submitting
            if (e.key === 'Enter' && e.ctrlKey) {
                handleNext();
            } else if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, handleNext, onClose]);

    if (!isOpen) return null;

    const renderStepContent = () => {
        const props: StepProps = { data: wizardData, updateData, goToStep };
        switch (currentStep) {
            case 1: return <OrderSelectionStep {...props} />;
            case 2: return <ItemSelectionStep {...props} />;
            case 3: return <ImageUploadStep {...props} highlightLabels={showLabelError} highlightImages={showImageError} />;
            case 4: return <ReviewStep {...props} highlightRefund={showLabelError} />;
            default: return null;
        }
    };

    const modalContent = (
        <div className={styles.overlay}>
            <div className={styles.modalContainer} style={{ position: 'relative' }}>
                {activeComplaintId && uploadTasks[activeComplaintId] && (
                    <div className={styles.uploadOverlay}>
                        <div className={styles.uploadStatusText}>
                            Enviando imagens... ({uploadTasks[activeComplaintId].uploadedFiles} de {uploadTasks[activeComplaintId].totalFiles})
                        </div>
                        <div className={styles.uploadProgressContainer}>
                            <div className={styles.progressBar}>
                                <div
                                    className={styles.progressFill}
                                    style={{
                                        width: `${(uploadTasks[activeComplaintId].uploadedFiles / uploadTasks[activeComplaintId].totalFiles) * 100}%`
                                    }}
                                />
                            </div>
                        </div>
                        <div className={styles.uploadSubText}>
                            Por favor, não feche esta aba até concluir o envio.
                        </div>
                    </div>
                )}

                <div className={styles.header}>
                    <h2 className={styles.title}>Nova reclamação</h2>
                    <button 
                        className={styles.closeButton} 
                        onClick={onClose} 
                        title="Fechar (Esc)"
                        disabled={isSubmitting}
                    >
                        <CloseIcon />
                    </button>
                </div>

                <div className={styles.stepperHeader}>
                    {STEPS.map(step => (
                        <div
                            key={step.id}
                            className={`${styles.stepIndicator} ${currentStep === step.id ? styles.stepActive : ''} ${currentStep > step.id ? styles.stepCompleted : ''}`}
                        >
                            <div className={styles.stepCircle}>
                                {currentStep > step.id ? <CheckIcon fontSize="small" /> : step.id}
                            </div>
                            <span className={styles.stepLabel}>{step.label}</span>
                        </div>
                    ))}
                </div>

                <div className={styles.content}>
                    {renderStepContent()}
                </div>

                <div className={styles.footer}>
                    <div className={styles.shortcutHint}>
                        Ctrl + Enter para avançar
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                            className={styles.backButton}
                            onClick={handleBack}
                            disabled={currentStep === 1}
                            style={{ visibility: currentStep === 1 ? 'hidden' : 'visible' }}
                        >
                            <ArrowBackIcon fontSize="small" style={{ marginRight: 8 }} />
                            Voltar
                        </button>

                        <button
                            className={styles.nextButton}
                            onClick={handleNext}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Enviando...' : (currentStep === 4 ? 'Concluir' : 'Próximo')}
                            {currentStep < 4 && !isSubmitting && <ArrowForwardIcon fontSize="small" />}
                        </button>
                    </div>
                </div>
            </div>
            <NotificationToastContainer
                toasts={toasts}
                onClose={removeToast}
                onClick={() => { }}
            />
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default NewComplaintModal;
